import { useState, useEffect } from 'react';
import { schemesApi } from '@/services/api';
import { formatSchemeLabel } from '@/utils/formatters';

export interface SchemeOption {
  value: string;
  label: string;
  fullName: string;
  region: number;
  investmentStrategy: string;
  investmentStrategyDisplay: string;
  orderCalcType: number;
  firmType?: number | null;
}

interface UseSchemeOptionsResult {
  schemeOptions: SchemeOption[];
  loading: boolean;
  error: string | null;
}

export function useSchemeOptions(): UseSchemeOptionsResult {
  const [schemeOptions, setSchemeOptions] = useState<SchemeOption[]>([]);
  // True from the first render — the fetch starts on mount, so there is no idle state to show.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    schemesApi
      .getSchemes({ page_size: 1000, is_active: true, ordering: 'code' })
      .then(response => {
        if (cancelled) return;
        setSchemeOptions(
          response.results.map(s => ({
            value: s.id.toString(),
            label: formatSchemeLabel(s),
            fullName: (s.full_name || s.name || '') as string,
            region: s.region ?? 1,
            investmentStrategy: s.investment_strategy != null ? String(s.investment_strategy) : '',
            investmentStrategyDisplay: s.investment_strategy_display || 'No Strategy',
            orderCalcType: s.order_calc_type ?? 1,
            firmType: s.strategy_firm_type ?? null,
          }))
        );
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Failed to load schemes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { schemeOptions, loading, error };
}